import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';

import { firebaseAuth, logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';
import { useSyncClient } from '~/hooks/models/useClient';
import {
    CONSENT_OPEN_EVENT,
    ConsentChoice,
    onConsentChange,
    readConsent,
    writeConsent,
} from '~/services/consent/consent';
import { analyticsAllowed, bannerAllowed, isBannerPath, isLegacyAccount } from '~/services/consent/consentScope';
import { startProductAnalytics, stopProductAnalytics } from '~/services/consent/productAnalytics';
import { ConsentBanner, ConsentDraft } from './ConsentBanner';

const ROUTE_CLIENT = /^\/(?:app|animator)\/([^/?#]+)/i;

/**
 * Monté une fois dans MainRoutes (animateur connecté). Affiche le bandeau là où c'est permis
 * (consentScope.ts) et démarre / arrête l'enregistrement des sessions selon le choix.
 */
export function ConsentLayer() {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();
    const { auth } = useAuth();
    const [storedClientID] = useLocalStorage('clientID', '');
    const clientID = ROUTE_CLIENT.exec(pathname)?.[1] || storedClientID || undefined;
    const client = useSyncClient(clientID || '');
    const mode = client?.mode;
    const scope = { path: pathname, clientID, mode };

    const [choice, setChoice] = useState<ConsentChoice | null>(() => readConsent());
    const [reopened, setReopened] = useState(false);
    useEffect(() => onConsentChange(setChoice), []);
    useEffect(() => {
        const open = () => setReopened(true);
        window.addEventListener(CONSENT_OPEN_EVENT, open);
        return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
    }, []);

    // Tant que la fiche client du BON lieu n'est pas lue, on ne sait pas si c'est un aéroport : rien
    // ne tourne (au changement de lieu, l'ancienne fiche reste affichée un instant avec l'ancien mode).
    const clientKnown = !!client && !!clientID && client.id === clientID;
    const allowed = clientKnown && analyticsAllowed(scope);
    const uid = auth?.uid;
    const venue = client?.name;
    // Compte d'avant le bandeau sans choix enregistré : accord réputé acquis (bandeau d'information).
    const legacy = isLegacyAccount(firebaseAuth.currentUser?.metadata.creationTime);
    const effective = choice ?? (legacy ? { ads: true, product: true } : null);

    useEffect(() => {
        if (allowed && effective?.product && uid) {
            startProductAnalytics({ uid, clientID, venue, mode, lang: i18n.language });
        } else {
            // Refus = on oublie tout ; hors périmètre ou lieu pas encore confirmé = simple pause.
            stopProductAnalytics({ forget: !!choice && !choice.product });
        }
    }, [allowed, choice, effective?.product, uid, clientID, venue, mode, i18n.language]);

    // Déconnexion (MainRoutes remplacé par AuthRoutes sans recharger) : plus rien ne doit tourner
    // pour l'écran de connexion ni pour le compte suivant sur la même TV.
    useEffect(() => () => stopProductAnalytics({ forget: true }), []);

    // « Modifier mes choix » rouvre le bandeau même si le lieu de cette page n'est pas celui du
    // téléphone ou de la TV (la carte de Mon compte a déjà vérifié le lieu affiché).
    const showBanner = reopened ? isBannerPath(pathname) : clientKnown && bannerAllowed(scope) && !choice;
    if (!showBanner) return null;

    const done = (draft: ConsentDraft) => {
        writeConsent(draft);
        setReopened(false);
        // Compteur anonyme des choix (bilan du taux de refus, lot 6 étape 4) : GA4 reste hors bandeau (D4).
        logEvent('consent_choice', 'Consent', {
            ads: draft.ads ? 1 : 0,
            product: draft.product ? 1 : 0,
            notice: !choice && legacy && !reopened ? 1 : 0,
        });
        // Pixel déjà chargé sur cette page : Meta cesse d'envoyer (révocation officielle du pixel).
        if (!draft.ads) (window as any).fbq?.('consent', 'revoke');
    };

    return (
        <ConsentBanner
            variant={!choice && legacy && !reopened ? 'notice' : 'choice'}
            initial={effective ? { ads: effective.ads, product: effective.product } : undefined}
            startCustomizing={reopened}
            onDone={done}
        />
    );
}
